import { Component } from '@angular/core';
import Highcharts from 'highcharts';
import { ChartSeries, chartColors } from '../report-aggregation';
import { ChartBlockBase } from './chart-block-base';
import {
  PANEL_SPACING,
  axisMaxFor,
  categoryLabelStyle,
  clampLabel,
  endLabelDataLabels,
  BAR_BORDER_RADIUS,
  endLabelMargins,
  categoryAxis,
  hiddenPercentAxis,
  isHorizontal,
  labelFitFor,
  lineHeightFor,
  textWidth,
} from './chart-layout';
import type { LabelFit } from './chart-layout';

interface Panel {
  label: string;
  /** Null when the variable does not resolve, which the template renders as "No data". */
  options: Highcharts.Options | null;
}

/** What one orientation's sizing pass works out, and every panel then shares. */
interface PanelLayout {
  fit: LabelFit;
  /** Where a category label wraps -- the gutter on a horizontal panel, a column on a vertical one. */
  wrapWidth: number;
  height: number;
  /** Only a horizontal panel pins its gutter; a vertical one has none to pin. */
  marginLeft?: number;
  /** Room kept for the percentage printed past the end of a bar. */
  endReserve: number;
}

const GRID_GAP = 20;
/** Must match .panel's padding below. */
const PANEL_PADDING = 8;
// Everything between the grid cell and the chart's plot: the panel's own padding, plus the chart's
// internal spacing. Derived rather than a literal, because it feeds the category-label wrap maths
// -- changing either without the other resizes the charts but not the labels they have to fit.
const PANEL_INSET = PANEL_PADDING * 2 + PANEL_SPACING[1] + PANEL_SPACING[3];

// Every size below is a budget the panel is fitted into, not a fixed dimension: a panel takes the
// height its own labels and categories need and stops at MAX_PANEL_HEIGHT, where the labels start
// truncating instead. Nothing is padded "just in case" -- a two-option panel with short answers
// still comes out small.

/** Grid-cell width below which a panel stops being readable, so a column is dropped instead. */
const MIN_PANEL_WIDTH = 200;
const MIN_PANEL_WIDTH_COMPACT = 150;
/**
 * The share of a horizontal panel the bars keep whatever the labels want. Without a ceiling on the
 * gutter Highcharts sizes it from the widest label, and one long answer leaves no plot at all.
 */
const MIN_PLOT_SHARE = 0.5;
/** Between the label gutter and the plot. */
const LABEL_GAP = 6;
/** A horizontal category's slot: below this the bar inside it stops reading as a bar. */
const MIN_SLOT_PX = 22;
/** And below this it stops reading as a bar in the other direction. */
const MIN_PLOT_PX = 56;
/** A vertical category's column. */
const MIN_COLUMN_PX = 14;
/** The plot's own floor, so a two-option vertical panel still looks like a chart. */
const MIN_PLOT_HEIGHT = 90;
/** Panel height outside the plot: the chart's top and bottom spacing plus the end-label margin. */
const CHROME_H = 42;
const CHROME_V = 34;
/**
 * Where label growth stops and truncating starts. A soft cap on purpose: it governs how many lines
 * a label may have, never how much room the bars get. A panel with twenty categories still gets
 * MIN_SLOT_PX for each of them and comes out taller than this -- twenty options is a topN decision,
 * and squeezing them into a fixed box is the bug being fixed, not the fix.
 *
 * Safe as a soft cap only because nothing paginates these decks: no slide canvas, no export. If one
 * ever lands, this constant becomes load-bearing.
 */
const MAX_PANEL_HEIGHT = 320;
const MIN_PANEL_HEIGHT = 96;
/** Ceiling on wrapped label lines, whatever the height budget allows. */
const MAX_LABEL_LINES = 4;
const MAX_LABEL_LINES_VERTICAL = 3;

@Component({
  selector: 'app-small-multiples-block',
  standalone: false,
  template: `
    @if (needsGroupBy) {
      <p class="grey4 nav-font12 mb-0">
        No grouping question chosen, so there are no groups to split this into. Pick one under
        <em>One panel per</em> under Edit module.
      </p>
    }
    <div class="panel-grid"
         [style.gap.px]="gridGap"
         [style.grid-template-columns]="'repeat(' + columns + ', minmax(0, 1fr))'">
      @for (panel of panels; track panel.label) {
        <div class="panel">
          <!-- Clamped rather than wrapped freely: a groupBy option can be a whole sentence, and one
               verbose group name would push its panel's chart below every other panel in the row. -->
          <p class="panel-title grey5 nav-font12 font-weight-bold mb-1" [title]="panel.label">{{ panel.label }}</p>
          @if (panel.options) {
            <!-- Rebuilt, not updated, when the panels are turned: see the bar chart block. -->
            @for (turn of [orientation]; track turn) {
              <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="panel.options"
                                style="width: 100%; display: block;"></highcharts-chart>
            }
          } @else {
            <p class="grey4 nav-font12 mb-0">No data</p>
          }
        </div>
      }
    </div>
  `,
  // Inline by default, which would make the width measured in ChartBlockBase 0.
  styles: [`
    :host { display: block; }
    .panel-grid {
      display: grid;
    }
    /* No border: the grid gap and each panel's own chart do the separating. */
    .panel {
      border-radius: 6px;
      padding: 8px; /* PANEL_PADDING */
    }
    .panel-title {
      line-height: 1.2;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `]
})
export class SmallMultiplesBlockComponent extends ChartBlockBase {
  highcharts = Highcharts;
  panels: Panel[] = [];
  orientation: 'horizontal' | 'vertical' = 'horizontal';
  columns = 2;
  readonly gridGap = GRID_GAP;
  /** Reachable by switching type inline, where groupBy starts empty. */
  needsGroupBy = false;

  protected build() {
    if (this.block.reportType !== 'subgroup-panels') return;
    const { orientation, columns, groupBy } = this.block.reportExtraData;
    this.needsGroupBy = !groupBy;
    const horizontal = isHorizontal(this.block.reportType, orientation);
    this.orientation = horizontal ? 'horizontal' : 'vertical';
    // With labels off a panel keeps only the room its bars need -- on a small panel, most of it.
    const hideLabels = !!this.block.reportExtraData.hideLabels;

    const width = this.hostWidth();
    const count = Math.max(1, this.series.length);
    const minPanel = this.compact ? MIN_PANEL_WIDTH_COMPACT : MIN_PANEL_WIDTH;

    // How many columns the block is actually wide enough for. An explicit `columns` from the design
    // is honoured up to this and no further: three columns of a half-width block is three slivers,
    // and the generated design has no way of knowing how wide the block ended up.
    const fitColumns = Math.max(1, Math.floor((width + GRID_GAP) / (minPanel + GRID_GAP)));
    this.columns = Math.min(4, fitColumns, Math.max(1, columns ?? Math.min(2, count)));

    // The panel width has to be known now, not at render, which is why the grid uses an explicit
    // column count rather than the `auto-fit` the donut grid gets away with: these panels carry
    // wrapped category labels whose width is a share of this. hostWidth() rather than plotWidth()
    // -- the chart spacing plotWidth() removes is charged per panel below, in PANEL_INSET.
    const panelWidth = Math.max(
      80, (width - GRID_GAP * (this.columns - 1)) / this.columns - PANEL_INSET);

    // One scale for every panel, taken across all of them. This is the defining property of a small
    // multiple: let each panel scale to its own peak and the grid becomes a set of unrelated charts
    // that still look comparable.
    const axisMax = axisMaxFor(this.series);

    const labelSize = this.compact ? 10 : 11;
    const fontFamily = this.fontFamily();

    // Every panel is aligned to the same spine, so the categories -- and therefore the label
    // measurements -- are identical across the grid. Measure once.
    const categories = (this.series.find(s => s.points.length)?.points ?? []).map(p => p.label);
    const slots = Math.max(1, ...this.series.map(s => s.points.length), this.alignCount ?? 0);

    const layout = horizontal
      ? this.horizontalLayout(panelWidth, slots, categories, labelSize, fontFamily)
      : this.verticalLayout(panelWidth, slots, categories, labelSize, fontFamily);

    // What each label reads as once it has been cut to the lines it was given. Keyed by the full
    // text, which stays the chart's category so the tooltip can still show all of it.
    const labelText = new Map(categories.map(category => [
      category,
      clampLabel(category, layout.wrapWidth, layout.fit.lines, labelSize, fontFamily),
    ]));

    const roles = chartColors(this.palette, Math.max(1, categories.length));

    this.panels = this.series.map(s => ({
      label: s.name,
      options: s.known
        ? this.panel(s, { horizontal, axisMax, labelSize, roles, labelText, hideLabels, ...layout })
        : null,
    }));
  }

  /**
   * A horizontal panel labels down its left, where Highcharts sizes the gutter from whatever the
   * labels measure -- so a long answer, or a single unbreakable word, takes the room the bars
   * needed and the bar disappears. The gutter is therefore budgeted here and set explicitly, and
   * any label that will not fit it is truncated rather than allowed to push the plot away.
   */
  private horizontalLayout(
    panelWidth: number, slots: number, categories: string[], labelSize: number, fontFamily: string,
  ): PanelLayout {
    // The percentage printed past the end of a bar; measured rather than the full-size chart's
    // 46px, which on a mini panel is a third of the plot.
    const endReserve = Math.ceil(textWidth('100%', labelSize, fontFamily)) + 8;

    // What the labels may take. The bars' claim is settled first and out of both ends, so the
    // gutter can never be sized to the text at the plot's expense -- that is the reported bug.
    const budget = Math.max(40, Math.min(
      Math.floor(panelWidth * (1 - MIN_PLOT_SHARE)),
      panelWidth - endReserve - MIN_PLOT_PX - LABEL_GAP));

    // Lines come out of the height budget rather than a fixed ceiling: three categories can afford
    // four-line labels inside the cap, twelve categories can afford one.
    const lineHeight = lineHeightFor(labelSize);
    const maxLines = Math.max(1, Math.min(
      MAX_LABEL_LINES,
      Math.floor(((MAX_PANEL_HEIGHT - CHROME_H) / slots - 4) / lineHeight)));

    const fit = labelFitFor({
      plotWidth: panelWidth,
      slots,
      categories,
      labelSize,
      horizontal: true,
      horizontalWidth: budget,
      fontFamily,
      maxLines,
    });

    // No wider than the budget, and no wider than the labels actually need -- short answers should
    // give their space back to the bars rather than hold a gutter open. The couple of pixels are
    // slack between what canvas measures and what the SVG renderer draws; without them a label
    // sized to exactly its own width can wrap one word onto a line nothing reserved room for.
    const gutter = Math.min(budget, (fit.widest ?? budget) + 2);
    const rowHeight = Math.max(MIN_SLOT_PX, fit.lines * lineHeight + 4);

    return {
      fit,
      wrapWidth: gutter,
      // The floor is what every category needs; the cap only ever bites on the label lines above,
      // so it can never take a bar's room away.
      height: Math.max(MIN_PANEL_HEIGHT, Math.round(slots * rowHeight + CHROME_H)),
      marginLeft: gutter + LABEL_GAP,
      endReserve,
    };
  }

  /**
   * A vertical panel labels along its bottom, where each category gets only its own column's share
   * of the width. The plot itself grows with the number of columns -- it used to be a flat height,
   * so an eight-option panel was drawn in the room two options need.
   */
  private verticalLayout(
    panelWidth: number, slots: number, categories: string[], labelSize: number, fontFamily: string,
  ): PanelLayout {
    const lineHeight = lineHeightFor(labelSize);
    const plotHeight = Math.max(MIN_PLOT_HEIGHT, slots * MIN_COLUMN_PX);
    const maxLines = Math.max(1, Math.min(
      MAX_LABEL_LINES_VERTICAL,
      Math.floor((MAX_PANEL_HEIGHT - CHROME_V - plotHeight) / lineHeight)));

    const fit = labelFitFor({
      plotWidth: panelWidth,
      slots,
      categories,
      labelSize,
      horizontal: false,
      horizontalWidth: 0,
      fontFamily,
      maxLines,
    });

    return {
      fit,
      wrapWidth: fit.wrapWidth,
      height: Math.max(
        MIN_PANEL_HEIGHT, Math.round(plotHeight + fit.lines * lineHeight + CHROME_V)),
      marginLeft: undefined,
      endReserve: labelSize + 12,
    };
  }

  private panel(
    series: ChartSeries,
    ctx: PanelLayout & {
      horizontal: boolean;
      axisMax: number;
      labelSize: number;
      roles: string[];
      labelText: Map<string, string>;
      hideLabels: boolean;
    },
  ): Highcharts.Options {
    const {
      horizontal, axisMax, labelSize, roles, labelText, fit, wrapWidth, height, marginLeft,
      endReserve, hideLabels,
    } = ctx;
    const type = horizontal ? 'bar' : 'column';

    return this.deckMotion({
      chart: {
        type,
        height,
        spacing: PANEL_SPACING,
        // Set rather than left to Highcharts, which would size the gutter from the labels and let
        // one long answer squeeze the plot to nothing.
        marginLeft: hideLabels ? undefined : marginLeft,
        ...endLabelMargins(horizontal, endReserve),
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: categoryAxis({
        // The full text stays the category, so the tooltip carries what the label had to drop.
        categories: series.points.map(p => p.label),
        labels: {
          step: 1,
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            const value = String(this.value);
            return labelText.get(value) ?? value;
          },
          style: categoryLabelStyle({ ...fit, wrapWidth }, labelSize),
        },
      }, !hideLabels),
      yAxis: hiddenPercentAxis(axisMax),
      tooltip: {
        // Wrapped, because the answer the axis label had to cut can be a whole sentence.
        style: { width: 260, whiteSpace: 'normal' },
        pointFormat: '<b>{point.y:.0f}%</b> ({point.custom.count})',
      },
      plotOptions: {
        // A bar begins exactly on the plot's left edge, so the tip of its rounded start corner
        // falls on the clip boundary and gets shaved -- that end then reads as squarer than the
        // other. clip:false removes the boundary; nothing can overflow it anyway, since the axis
        // maximum is derived from the data.
        series: { borderWidth: 0, clip: false },
        // Thickness stays proportional here, unlike the full-size bar blocks: a panel is a
        // miniature and a flat 15px bar would not fit several of them to a row. The corner is
        // shared so the shapes still read as the same family.
        bar: { pointPadding: 0.08, groupPadding: 0.06, borderRadius: BAR_BORDER_RADIUS },
        column: { pointPadding: 0.08, groupPadding: 0.06, borderRadius: BAR_BORDER_RADIUS },
      },
      series: [{
        type,
        name: series.name,
        dataLabels: endLabelDataLabels(labelSize),
        // Coloured by answer option rather than by position, so an option keeps its colour in every
        // panel -- across a grid that is what lets the shapes be read against each other.
        data: series.points.map(p => {
          const index = Math.max(0, p.optionIndex);
          return {
            y: p.percent,
            custom: { count: p.value },
            colorIndex: index,
            color: roles[index] ?? roles[roles.length - 1],
            className: p.optionIndex < 0 ? 'chart-track' : undefined,
          };
        }),
      }] as Highcharts.SeriesOptionsType[],
    });
  }
}
